/* ============================================================================
   <rtb-marquee> — the endless logo run used by the brand strip and the
   "As seen in" press strip.

   Load it with `defer` (classic script, not a module):
       <script src="{{ 'rtb-marquee.js' | asset_url }}" defer></script>

   RELATIONSHIP TO HORIZON'S assets/marquee.js
   Horizon's marquee is read and deliberately NOT reused, for three reasons:
     1. It imports '@theme/component' and '@theme/utilities' and inherits a
        refs/MutationObserver base class — a hard coupling to Horizon internals
        that the RTB sections are supposed to stay additive to.
     2. It SLOWS DOWN on pointerenter (playback rate animated to 0 over 500ms
        and it never actually reaches a stop). RTB pauses for real.
     3. It clones with aria-hidden but leaves the clone's links tabbable, so a
        keyboard user walks every logo link twice.
   What IS carried over from it: the duplicate-the-content-and-translate-50%
   mechanic, the aria-hidden clone, and recomputing on resize.

   ACCESSIBILITY / SEO CONTRACT
   · The clone is aria-hidden="true" and every focusable inside it gets
     tabindex="-1", so nine logos do not become eighteen announced links and
     eighteen duplicate alt strings (which reads as keyword repetition).
   · ids inside the clone are stripped so nothing is duplicated.
   · WCAG 2.2.2 (motion longer than five seconds): :hover and :focus-within
     pause the run in CSS (rtb-components.css), and a visible pause/play
     button, added here, stays paused for touch users. Its labels come from
     the element's data-pause-label / data-play-label (locale strings); it is
     shown only while the run animates.
   · Under prefers-reduced-motion: reduce the run does not animate AND is not
     cloned at all — a stopped duplicate is just a second copy of the logos on
     screen.

   EXPECTED MARKUP
     <rtb-marquee class="rtb-marquee" data-speed="60" role="group" aria-label="Our brands">
       <div class="rtb-marquee__run" data-rtb-marquee-run>
         <ul class="rtb-marquee__track" data-rtb-marquee-track role="list">
           <li><a href="…"><img src="…" alt="STANLEY Jr."></a></li>
         </ul>
       </div>
     </rtb-marquee>

   data-speed is pixels per second (default 60). The duration is derived from
   the real track width so a strip of 3 logos and a strip of 9 move at the same
   perceived speed.
   ============================================================================ */

(function () {
  'use strict';

  if (customElements.get('rtb-marquee')) return;

  var DEFAULT_SPEED = 60;
  var CLONE_CLASS = 'rtb-marquee__track--clone';
  var SVG_NS = 'http://www.w3.org/2000/svg';
  var PAUSE_ICON = 'M7 5h3v14H7zM14 5h3v14h-3z';
  var PLAY_ICON = 'M8 5v14l11-7z';

  class RtbMarquee extends HTMLElement {
    connectedCallback() {
      this.run = this.querySelector('[data-rtb-marquee-run]');
      this.track = this.querySelector('[data-rtb-marquee-track]');
      if (!this.run || !this.track) return;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this._onMotionChange = this._sync.bind(this);
      this._onBlockSelect = this._handleBlockSelect.bind(this);
      this._onBlockDeselect = this._handleBlockDeselect.bind(this);

      if (typeof this.reducedMotion.addEventListener === 'function') {
        this.reducedMotion.addEventListener('change', this._onMotionChange);
      }

      this._paused = false;
      this._onToggle = this._toggle.bind(this);
      this._addToggle();

      if (typeof ResizeObserver === 'function') {
        this._resizeObserver = new ResizeObserver(this._onResize.bind(this));
        this._resizeObserver.observe(this.track);
        this._resizeObserver.observe(this);
      }

      document.addEventListener('shopify:block:select', this._onBlockSelect);
      document.addEventListener('shopify:block:deselect', this._onBlockDeselect);

      this._sync();
    }

    disconnectedCallback() {
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
        this._resizeObserver = null;
      }
      if (this.reducedMotion && this._onMotionChange && typeof this.reducedMotion.removeEventListener === 'function') {
        this.reducedMotion.removeEventListener('change', this._onMotionChange);
      }
      document.removeEventListener('shopify:block:select', this._onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this._onBlockDeselect);

      this._removeClone();
      if (this._toggleButton) {
        this._toggleButton.removeEventListener('click', this._onToggle);
        this._toggleButton.remove();
        this._toggleButton = null;
      }

      this._onToggle = null;
      this._onMotionChange = null;
      this._onBlockSelect = null;
      this._onBlockDeselect = null;
      this.run = null;
      this.track = null;
    }

    _sync() {
      if (this.reducedMotion.matches) {
        this._removeClone();
        this.setAttribute('data-motion', 'off');
        this._syncToggle();
        return;
      }

      // Clone FIRST, flag second. The CSS animation is gated on
      // `[data-motion="on"]`, so the run never translates until the second copy
      // of the track exists behind it — before this line (deferred script, or
      // JS blocked entirely) the strip is static rather than sliding its only
      // copy off-screen and leaving a blank band.
      this._addClone();
      this._setDuration();
      this.setAttribute('data-motion', 'on');
      this._syncToggle();
    }

    _onResize() {
      this._setDuration();
      this._syncToggle();
    }

    /**
     * WCAG 2.2.2: hover and focus pause the run, but a touch user needs a
     * control that stays paused. The run is paused inline because the
     * stylesheet's `animation` shorthand would reset any weaker rule.
     */
    _addToggle() {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'rtb-marquee__toggle';
      Object.assign(button.style, {
        position: 'absolute',
        insetBlockEnd: '4px',
        insetInlineEnd: '4px',
        zIndex: '1',
        display: 'none',
        alignItems: 'center',
        justifyContent: 'center',
        width: '28px',
        height: '28px',
        padding: '0',
        border: '0',
        borderRadius: '50%',
        background: 'var(--rtb-white, #fff)',
        color: 'var(--rtb-ink, #1d1b20)',
        cursor: 'pointer'
      });

      var svg = document.createElementNS(SVG_NS, 'svg');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('width', '16');
      svg.setAttribute('height', '16');
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('focusable', 'false');
      this._toggleIcon = document.createElementNS(SVG_NS, 'path');
      this._toggleIcon.setAttribute('fill', 'currentColor');
      svg.appendChild(this._toggleIcon);
      button.appendChild(svg);

      button.addEventListener('click', this._onToggle);
      if (getComputedStyle(this).position === 'static') this.style.position = 'relative';
      this.appendChild(button);
      this._toggleButton = button;
      this._renderToggle();
    }

    _toggle() {
      this._paused = !this._paused;
      this.run.style.animationPlayState = this._paused ? 'paused' : '';
      this._renderToggle();
    }

    _renderToggle() {
      var label = this._paused
        ? this.getAttribute('data-play-label') || 'Play animation'
        : this.getAttribute('data-pause-label') || 'Pause animation';
      this._toggleButton.setAttribute('aria-label', label);
      this._toggleIcon.setAttribute('d', this._paused ? PLAY_ICON : PAUSE_ICON);
    }

    /**
     * Only offer the control while the run actually moves. An animation name
     * is not enough: a layout that turns the run into a static grid (the
     * brand marquee below 990px) gives it `display: contents`, so it has no
     * box to translate and the animation is inert — a pause button there
     * pauses nothing.
     */
    _syncToggle() {
      if (!this._toggleButton || !this.run) return;
      var runStyle = getComputedStyle(this.run);
      var moving =
        this.getAttribute('data-motion') === 'on' &&
        runStyle.animationName !== 'none' &&
        runStyle.display !== 'contents' &&
        runStyle.display !== 'none';
      this._toggleButton.style.display = moving ? 'inline-flex' : 'none';
    }

    _addClone() {
      if (this.run.querySelector('.' + CLONE_CLASS)) return;

      var clone = this.track.cloneNode(true);

      clone.classList.add(CLONE_CLASS);
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('data-rtb-marquee-track');
      clone.removeAttribute('id');

      // The clone must be invisible to the tab order and to the a11y tree, or
      // every logo link is announced and focusable twice.
      clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach(function (node) {
        node.setAttribute('tabindex', '-1');
      });
      clone.querySelectorAll('[id]').forEach(function (node) {
        node.removeAttribute('id');
      });

      this.run.appendChild(clone);
    }

    _removeClone() {
      if (!this.run) return;
      var clone = this.run.querySelector('.' + CLONE_CLASS);
      if (clone) clone.remove();
    }

    /** Constant perceived speed regardless of how many logos the strip holds. */
    _setDuration() {
      if (!this.track) return;

      var speed = parseFloat(this.getAttribute('data-speed'));
      if (!speed || speed <= 0) speed = DEFAULT_SPEED;

      var width = this.track.getBoundingClientRect().width;
      if (!width) return;

      this.style.setProperty('--rtb-marquee-duration', Math.round(width / speed) + 's');
    }

    /**
     * In the theme editor, stop the run while a block is selected so the
     * merchant can actually look at the logo they are editing, and bring it
     * into view.
     */
    _handleBlockSelect(event) {
      var target = event.target;
      if (!(target instanceof Element) || !this.contains(target)) return;

      this.setAttribute('data-editor-paused', 'true');
      if (typeof target.scrollIntoView === 'function') {
        target.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'center' });
      }
    }

    _handleBlockDeselect(event) {
      var target = event.target;
      if (!(target instanceof Element) || !this.contains(target)) return;
      this.removeAttribute('data-editor-paused');
    }
  }

  customElements.define('rtb-marquee', RtbMarquee);
})();
